import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useLocalState } from '../../../../shared/UseLocalState'
import { BookedService, Person, PersonApi, ServiceDto, Starter } from '../../../../api'
import { authState } from '../../../../state/Auth.state'
import { Loading } from '../../../loading/Loading'
import { DataTable } from 'primereact/datatable'
import { registrationState } from '../Registration.state'
import { Column } from 'primereact/column'
import { appState } from '../../../../state/App.state'
import { actualShowState } from '../../../../state/ActualShow.state'
import { ServiceSelectionPanel } from './ServiceSelectionPanel'
import { HorseByRegId } from '../../../../shared/HorseByRegId'
import { PersonByRegId } from '../../../../shared/RiderByRegId'

export const RiderScreen = () => {
  /* STATE */
  const user = useLocalState<Person | null>(authState.user$, authState.user$.value)
  const selectedUser = useLocalState<Person | null>(registrationState.selectedRider$, registrationState.selectedRider$.value)
  const [regId, setRegId] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const additionalUsers = useLocalState<Person[]>(registrationState.additionalRiders$, registrationState.additionalRiders$.value)
  const selectedStarter = useLocalState<Starter | null>(registrationState.selectedStarter$, registrationState.selectedStarter$.value)
  const selectedHorse = useLocalState(registrationState.selectedHorse$, registrationState.selectedHorse$.value)
  const actualShowBookedServices = useLocalState(actualShowState.actualShowBookedServices$, actualShowState.actualShowBookedServices$.value)
  const actualShowStarters = useLocalState(actualShowState.actualShowStarters$, actualShowState.actualShowStarters$.value)
  const showServices = useLocalState<ServiceDto[]>(actualShowState.actualShowServices$, actualShowState.actualShowServices$.value)

  /* DERIVED STATE */
  const userOptions: Person[] = useMemo(() => {
    const all = user ? [user, ...additionalUsers] : [...additionalUsers]
    return all.filter((p, idx, arr) => arr.findIndex((p2) => p2?.registerId === p?.registerId) === idx)
  }, [user, additionalUsers])
  const bookedRiderServices = useMemo(
    () =>
      actualShowBookedServices?.filter((bs) => {
        const svcType = showServices?.find((so) => so?.id === bs?.serviceId)?.type
        return svcType === 'G' && bs?.riderId === selectedUser?.registerId
      }),
    [actualShowBookedServices, showServices, selectedUser]
  )

  // N-Services: wenn Starter aus Nachnennung ausgewählt → nur dieser, sonst alle des Reiters
  const starterNServices = useMemo(() => {
    const relevantStarters = selectedStarter
      ? actualShowStarters?.filter((s) => s?.id === selectedStarter?.id)
      : actualShowStarters?.filter((s) => s?.riderRegisterId === selectedUser?.registerId)
    return relevantStarters
      ?.map((s) => ({
        starter: s,
        services: actualShowBookedServices?.filter((bs) => {
          const svcType = showServices?.find((so) => so?.id === bs?.serviceId)?.type
          return svcType === 'N' && bs?.starterId === s?.id
        })
      }))
      .filter((entry) => (entry.services?.length ?? 0) > 0)
  }, [actualShowStarters, actualShowBookedServices, showServices, selectedUser, selectedStarter])

  // N schon gebucht für die aktuelle Pferd+Reiter-Kombination?
  const alreadyBookedN = useMemo(() => {
    const matchingStarter = actualShowStarters?.find(
      (s) => s?.riderRegisterId === selectedUser?.registerId && s?.horseRegisterId === selectedHorse?.registerId
    )
    if (!matchingStarter) return false
    return actualShowBookedServices?.some((bs) => {
      const svcType = showServices?.find((so) => so?.id === bs?.serviceId)?.type
      return svcType === 'N' && bs?.starterId === matchingStarter?.id
    })
  }, [actualShowStarters, actualShowBookedServices, showServices, selectedUser, selectedHorse])

  // N-Services automatisch vorauswählen sobald neue Startnummer buchbar
  useEffect(() => {
    if (!selectedHorse || !selectedUser || alreadyBookedN) return
    const nServices = showServices?.filter((s) => s?.type === 'N') ?? []
    if (nServices.length === 0) return
    registrationState.selectedServices$.next([
      ...registrationState.selectedServices$.value.filter((s) => {
        const svc = showServices?.find((so) => so?.id === s?.id)
        return svc?.type !== 'N'
      }),
      ...nServices.map((s) => ({ ...s, amount: 1 }))
    ])
  }, [selectedHorse, selectedUser, alreadyBookedN, showServices])

  /* LOGIC */
  const getRiderByRegId = useCallback(() => {
    if ([user, ...additionalUsers]?.some((horse) => horse?.registerId === regId))
      return appState.addToast('Reiter bereits in Tabelle!', 'warning')
    setLoading(true)
    new PersonApi(authState.apicfg())
      .apiPersonRegisterRegisterIdGet({
        registerId: regId
      })
      .then((p) => {
        appState.addToast('Reiter gefunden: ' + p?.firstName + ' ' + p?.lastName, 'info')
        registrationState.additionalRiders$.next([...registrationState.additionalRiders$.value, p])
        registrationState.selectedRider$.next(p)
      })
      .catch(() => {
        appState.addToast('Reiter mit RegId ' + regId + ' nicht gefunden', 'error')
      })
      .finally(() => setLoading(false))
  }, [regId])

  // Synchronisiere Reiter-Auswahl bei Starter-Wechsel oder wenn additionalUsers nachladen
  useEffect(() => {
    if (!selectedStarter) return
    const riderId = selectedStarter.riderRegisterId
    const allRiders = user ? [user, ...additionalUsers] : [...additionalUsers]
    const found = allRiders.find((r) => r?.registerId === riderId)
    if (found) {
      registrationState.selectedRider$.next(found)
    }
  }, [selectedStarter, additionalUsers, user])

  /* TEMPLATE */
  return (
    <>
      <Loading loading={loading} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <div>
          <DataTable
            showSelectAll={false}
            showHeaders={false}
            value={userOptions}
            selectionMode={'single'}
            selection={selectedUser}
            onSelectionChange={(e) => {
              // toggle: deselect if same rider clicked again
              if ((e.value as Person)?.registerId === selectedUser?.registerId) {
                registrationState.selectedRider$.next(null)
                const services = actualShowState.actualShowServices$.value
                registrationState.selectedServices$.next(
                  registrationState.selectedServices$.value.filter((s) => {
                    const svc = services?.find((so) => so?.id === s?.id)
                    return svc?.type !== 'N'
                  })
                )
                return
              }
              // check if horse and rider combination is already in starters
              const starters = actualShowState.actualShowStarters$.value
              if (
                starters?.some(
                  (s) =>
                    s?.riderRegisterId === e?.value?.registerId && s?.horseRegisterId === registrationState.selectedHorse$.value?.registerId
                )
              ) {
                appState.addToast(
                  `Pferd / Reiter Kombination bereits gebucht (Startnr. ${starters?.find((s) => s?.riderRegisterId === e?.value?.registerId && s?.horseRegisterId === registrationState.selectedHorse$.value?.registerId)?.startNo ?? '?'}). Bitte Buchung im Tab "Nachnennung" aktualisieren.`,
                  'warning'
                )
              } else {
                registrationState.selectedRider$.next(e.value as Person)
              }
            }}
            isDataSelectable={() => !selectedStarter}
          >
            <Column selectionMode='single' bodyStyle={{ width: '3rem' }} />
            <Column body={(person: Person) => `${person?.firstName} ${person?.lastName}`} />
          </DataTable>
        </div>
        <div>
          <p style={{ marginBottom: '0.5rem' }}>
            <b>Reiter über Registrierungs ID hinzufügen</b>
          </p>
          <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
            <InputText
              value={regId}
              onChange={(e) => setRegId(e?.target?.value ?? '')}
              style={{ flex: 1 }}
              placeholder='Registrierungs ID eingeben ...'
              onKeyDown={(e) => {
                if (e.key === 'Enter' && regId?.length > 1) getRiderByRegId()
              }}
            />
            <Button onClick={getRiderByRegId} disabled={regId?.length < 1} label='Suchen' />
          </div>
        </div>
      </div>

      {/* Schon gebuchte G-Services des ausgewählten Reiters – nur einmal buchbar, keine weitere Auswahl */}
      {bookedRiderServices?.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3>Gebuchte Services für {selectedUser?.firstName} {selectedUser?.lastName}</h3>
          <DataTable value={bookedRiderServices} style={{ width: '100%' }}>
            <Column
              header='Service'
              body={(service: BookedService) => showServices?.find((s) => s?.id === service?.serviceId)?.name}
            />
            <Column
              header='Menge'
              headerStyle={{ width: '4rem', textAlign: 'right' }}
              bodyStyle={{ width: '4rem', textAlign: 'right' }}
              body={(service: BookedService) => service?.quantity}
            />
            <Column
              header='Preis'
              headerStyle={{ width: '5rem', textAlign: 'right' }}
              bodyStyle={{ width: '5rem', textAlign: 'right', whiteSpace: 'nowrap' }}
              body={(service: BookedService) => service?.totalPrice?.toFixed(2).replace('.', ',') + '\u00a0€'}
            />
          </DataTable>
        </div>
      )}

      {/* Typ G nur buchbar wenn noch nicht gebucht */}
      {!(bookedRiderServices?.length > 0) && <ServiceSelectionPanel types={['G']} title='Services für den Reiter:' disabled={!!selectedStarter} />}

      {/* Gebuchte N-Services gruppiert nach Startnummer */}
      {(starterNServices?.length ?? 0) > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3>Gebuchte Startnummer-Services</h3>
          {starterNServices?.map(({ starter, services }) => (
            <div key={starter?.id} style={{ marginBottom: '1rem' }}>
              <p style={{ fontWeight: 'bold', margin: '0.5rem 0 0.25rem 0' }}>
                Nr. {starter?.startNo} – <HorseByRegId regId={starter?.horseRegisterId} /> / <PersonByRegId regId={starter?.riderRegisterId} />
              </p>
              <DataTable value={services} style={{ width: '100%' }}>
                <Column
                  header='Service'
                  body={(service: BookedService) => showServices?.find((s) => s?.id === service?.serviceId)?.name}
                />
                <Column
                  header='Menge'
                  headerStyle={{ width: '4rem', textAlign: 'right' }}
                  bodyStyle={{ width: '4rem', textAlign: 'right' }}
                  body={(service: BookedService) => service?.quantity}
                />
                <Column
                  header='Preis'
                  headerStyle={{ width: '5rem', textAlign: 'right' }}
                  bodyStyle={{ width: '5rem', textAlign: 'right', whiteSpace: 'nowrap' }}
                  body={(service: BookedService) => service?.totalPrice?.toFixed(2).replace('.', ',') + '\u00a0€'}
                />
              </DataTable>
            </div>
          ))}
        </div>
      )}

      {/* N buchbar für aktuelle Pferd+Reiter-Kombination, falls noch nicht gebucht */}
      {selectedHorse && !alreadyBookedN && (
        <ServiceSelectionPanel
          types={['N']}
          title={`Startnummer-Service für ${selectedHorse?.name}${selectedUser ? ` / ${selectedUser?.firstName} ${selectedUser?.lastName}` : ''}:`}
          disabled={!selectedUser || !!selectedStarter}
        />
      )}
    </>
  )
}
